package com.elan.edubridge.ui.member

import com.elan.edubridge.shared.domain.model.AttendanceStatus
import com.elan.edubridge.ui.shell.formatKstClockTime
import kotlin.js.Date

internal enum class NotificationCategory(val label: String) {
    ATTENDANCE("출결"),
    NOTICE("공지"),
    ASSIGNMENT("과제"),
}

internal data class MemberNotificationItem(
    val id: String,
    val category: NotificationCategory,
    val iconId: String,
    /** `.eb-notification-item__icon--{variant}` 수정자(가이드 §3.8): success | info | neutral. */
    val iconVariant: String,
    val title: String,
    val body: String,
    val timeLabel: String,
    val sortKey: Double,
    val groupLabel: String,
    val linkHref: String,
    val linkLabel: String? = null,
)

/**
 * 알림함(§3.8)·홈 카드 배지·상단 벨 카운트가 공유하는 데이터 소스.
 *
 * 개인 알림 도메인 모델이 아직 `shared`에 없어(§S1 범위 밖 — `NotificationSummary`는 학원 단위
 * 전달 통계일 뿐 개인 알림함이 아니다) 기존 3개 조회(출결 타임라인·공지·과제)를 조합해 여기서만
 * 합성한다. 실제 Firebase 전환 후에도 읽음 처리는 `readAt`만 갱신하는 계약(rules:58-60)이지만,
 * 지금은 InMemory 상태(로그인 계정 uid별 Set)로 같은 동작을 흉내낸다.
 */
internal object MemberNotifications {
    private val readIdsByUser = mutableMapOf<String, MutableSet<String>>()

    /** [MemberContext.timelineUseCase]는 항상 `session`으로만 조회하므로(§4.3 격리) 여기서도
     * 다른 사용자의 uid를 끼워 넣을 방법이 없다. */
    suspend fun loadAll(context: MemberContext): List<MemberNotificationItem> {
        val today = todayServiceDate()

        val attendanceItems = context.timelineUseCase(context.session, academyId = null, subjectStudentId = null, limit = 30)
            .map { entry ->
                val isCheckOut = entry.status == AttendanceStatus.LEFT
                MemberNotificationItem(
                    id = "attendance:${entry.eventId}",
                    category = NotificationCategory.ATTENDANCE,
                    iconId = if (isCheckOut) "icon-check-out" else "icon-check-in",
                    iconVariant = if (isCheckOut) "info" else "success",
                    title = if (isCheckOut) "하원 알림" else "등원 알림",
                    body = "${entry.subjectStudentName} 학생이 ${formatKstClockTime(entry.occurredAt)}에 ${entry.status.displayName}했습니다.",
                    timeLabel = formatKstClockTime(entry.occurredAt),
                    sortKey = Date(entry.occurredAt).getTime(),
                    groupLabel = if (entry.serviceDate == today) "오늘" else entry.serviceDate,
                    linkHref = "/app/me/schedule",
                    linkLabel = "출결 기록 보기",
                )
            }

        // 알림함은 여러 출처를 합쳐 만드는 파생 목록이라 출처 하나가 실패해도 나머지는 그린다.
        // 실패 자체는 각 원본 화면(공지·과제)이 그 화면의 방식으로 알린다 — 여기서 다시 알리면
        // 같은 사건에 두 번 경고하게 된다(#30).
        val noticeItems = context.subjectAcademyIds().flatMap { academyId ->
            context.repository.noticesResult(academyId).items.map { notice ->
                MemberNotificationItem(
                    id = "notice:${notice.id}",
                    category = NotificationCategory.NOTICE,
                    iconId = "icon-notice",
                    iconVariant = "neutral",
                    title = notice.title,
                    body = notice.content,
                    timeLabel = notice.date,
                    sortKey = Date(notice.date).getTime(),
                    groupLabel = if (notice.date == today) "오늘" else notice.date,
                    linkHref = "/app/me/notices",
                )
            }
        }

        val assignmentItems = context.subjectAssignmentTargets()
            .flatMap { (academyId, studentUserId) ->
                context.repository.studentAssignmentsResult(academyId, studentUserId).items
            }
            .distinctBy { it.id } // 자녀 여럿이 같은 학원일 때 InMemory 소스가 학생 무관하게 학원 전체를 돌려줄 수 있어 방어적으로 중복을 걷어낸다.
            .filter { !it.completed }
            .map { assignment ->
                MemberNotificationItem(
                    id = "assignment:${assignment.id}",
                    category = NotificationCategory.ASSIGNMENT,
                    iconId = "icon-assignment",
                    iconVariant = "neutral",
                    title = "과제 안내",
                    body = "${assignment.title} · 마감 ${assignment.dueDate}",
                    timeLabel = assignment.dueDate,
                    sortKey = Date(assignment.dueDate).getTime(),
                    groupLabel = if (assignment.dueDate == today) "오늘" else assignment.dueDate,
                    linkHref = "/app/me/assignments",
                )
            }

        return (attendanceItems + noticeItems + assignmentItems).sortedByDescending { it.sortKey }
    }

    fun unreadCount(userId: String, items: List<MemberNotificationItem>): Int =
        items.count { !isRead(userId, it.id) }

    fun isRead(userId: String, id: String): Boolean = readIdsByUser[userId]?.contains(id) == true

    fun markRead(userId: String, id: String) {
        readIdsByUser.getOrPut(userId) { mutableSetOf() }.add(id)
    }
}
